import {
  ComponentStructuredData,
  ComponentStructuredDataMapping,
  JSONType,
  SdsComponent,
  StructuredDataFieldComponentMapping,
} from 'interface';
import { WORKPLACE_SAFETY_ORIGIN } from '../constants';
import { renderSnackbar } from './renderSnackbar';

export const isMobile = (): boolean => {
  return window.matchMedia('(max-width: 767px)').matches;
};

export const getEnv = (): string => {
  const host = window.location.hostname;
  if (host.indexOf('localhost') > -1 || host.indexOf('127.0.0.1') > -1)
    return 'development';

  if (host.indexOf('staging') > -1) return 'staging';

  if (host.indexOf('rc') > -1) return 'rc';

  return 'production';
};

type PasswordErrors =
  | 'length'
  | 'lowercase'
  | 'uppercase'
  | 'digit'
  | 'character';

export const passwordValidity = (password: string) => {
  const pwdErrors: PasswordErrors[] = [];
  if (password.length < 10) pwdErrors.push('length');
  if (!/[a-z]/.test(password)) pwdErrors.push('lowercase');
  if (!/[A-Z]/.test(password)) pwdErrors.push('uppercase');
  if (!/[0-9]/.test(password)) pwdErrors.push('digit');
  if (!/[@!#$%^&*]/.test(password)) pwdErrors.push('character');
  return { pwdValid: pwdErrors.length === 0, pwdError: pwdErrors };
};

export const clearUserSessionFromStorage = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('impersonate_backup_token');
};

export const setUserSessionToStorage = (
  method?: string,
  refreshToken?: string | null,
  accessToken?: string | null
) => {
  method && localStorage.setItem('login_method', method);
  refreshToken && localStorage.setItem('refresh_token', refreshToken);
  accessToken && localStorage.setItem('access_token', accessToken);
};

export const isHomePage = () => window.location.pathname === '/';

export const isUnregisteredAuthoringService = (user: any) => {
  const authoringPlan = user?.customer?.authoring_subscription_plan?.plan?.plan;
  return !authoringPlan || authoringPlan === '';
};

export const isTrialPeriod = (user: any) => {
  return user?.customer?.authoring_subscription_plan?.is_trial_period;
};

export const isExpired = (user: any) => {
  return user?.customer?.authoring_subscription_plan?.expired;
};

export const trimString = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  return value.trim();
};

export const isForWorkplaceSafety = () => {
  const currentOrigin = window.location.origin;
  if (currentOrigin.includes(WORKPLACE_SAFETY_ORIGIN)) return true;
  return false;
};

export const multipleClasses = (arr: string[]) => {
  return arr.join(' ');
};

export const isValidNumber = (value: string) => {
  const num = Number(value);
  return !isNaN(num) && isFinite(num);
};

export const createUUID = (): string => {
  let dt = new Date().getTime();
  let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      let r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
};

export const deferImport = (promise: Promise<any>) => {
  return new Promise(resolve => {
    promise
      .then(res => {
        resolve(res);
      })
      .catch((error: Error) => {
        console.log(error.name);
        if (error.name === 'ChunkLoadError') {
          renderSnackbar(
            ['A new version is available'],
            'Update',
            () => window.location.reload(),
            'info-snackbar',
            false
          );
          resolve({ default: () => <div>A new version is available</div> });
        }

        renderSnackbar(
          ["Oh no, there seems to be a problem, we're working on it..."],
          'Close',
          undefined,
          'error-snackbar',
          false
        );
        resolve({
          default: (
            <div>
              Oh no, there seems to be a problem, we&#39;re working on it...
            </div>
          ),
        });
      });
  });
};

export function getStructuredDataForFields(
  structuredData?: ComponentStructuredDataMapping,
  components?: Array<SdsComponent>,
  fields?: Array<keyof ComponentStructuredData>
): StructuredDataFieldComponentMapping {
  const structuredDataByComponent: StructuredDataFieldComponentMapping = {};
  if (!structuredData || !components || !fields)
    return structuredDataByComponent;

  Object.keys(structuredData).map(componentId => {
    const componentStructuredData = structuredData[Number(componentId)];
    if (componentStructuredData) {
      const processedComponent = components.find(
        el => el.id?.toString() === componentId
      )?.component_name;
      if (processedComponent) {
        fields.forEach(field => {
          if (structuredDataByComponent[field] === undefined)
            structuredDataByComponent[field] = [];

          if ((componentStructuredData[field] as Array<JSONType>)?.length) {
            structuredDataByComponent[field]?.push({
              componentName: processedComponent,
              description: componentStructuredData[
                `${field}_desc` as keyof ComponentStructuredData
              ] as string | null,
              rows: componentStructuredData[field] as Array<JSONType>,
            });
          }
        });
      }
    }
  });
  return structuredDataByComponent;
}
